import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../Context/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import OtpInput from "./auth/OtpInput";
import { useTranslation } from "react-i18next";
import PasswordRequirementFeedback from "./auth/PasswordRequirementFeedback";
import {
  getPasswordRequirementFeedback,
  isStrongPassword,
} from "../auth/passwordPolicy";
import {
  login as loginApi,
  register as registerApi,
  socialLogin as socialLoginApi,
  verifyEmail as verifyEmailApi,
  resendVerificationEmail as resendVerificationEmailApi,
  forgotPassword as forgotPasswordApi,
  resetPassword as resetPasswordApi,
} from "../auth/authApi";
import { setTokens } from "../auth/authStorage";
import { useDebouncedEmailExistsCheck } from "../auth/useDebouncedEmailExistsCheck";
import {
  clearAuthSensitiveQueries,
  isUserDoesNotExistError,
} from "../auth/authSession";
import { trySyncExplorerPublicProfileBeforeSignup } from "../explorer/explorerPreLoginSignupSync";
import { isPaidPortalMember } from "../utils/membershipTier";
import {
  ExplorerProfileSyncError,
  explorerSignupPath,
  resolveExplorerGuardedPath,
  syncExplorerStateAfterAuth,
} from "../explorer/explorerProfileSync";
import {
  getExplorerPostMembershipEventCombo,
  getExplorerEventId,
  getExplorerLocalProfile,
  getExplorerPendingOnboarding,
} from "../explorer/explorerSession";
import axiosInstance, { getAPIURL } from "../api/axiosInstance";
import { getEarlyStageStatus } from "../api/earlyStageMembershipApi";
import { isEarlyStageRejectionNoticeVisible } from "../utils/earlyStageRejectionNotice";
import { shouldResumeEarlyStageRegistration } from "../utils/earlyStageRegistrationGate";
import SignupProfileStep, {
  isSignupProfileComplete,
} from "./auth/SignupProfileStep";
import {
  EMPTY_SIGNUP_PROFILE,
  SignupProfileFormValues,
} from "./auth/signupProfileConstants";
import {
  normalizeExplorerSegment,
  stageLabelForExplorerSegment,
} from "../explorer/explorerSegments";
import { isValidPhoneNumber } from "react-phone-number-input";
import LiveTranslationDropdown from "./common/LiveTranslationDropdown";
import "./css/AuthFlow.css";

type RegisterPhase = "account" | "profile" | "verify";

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function safeInternalPath(redirect: string | null): string {
  if (!redirect) return "";
  const t = redirect.trim();
  if (!t.startsWith("/") || t.startsWith("//")) return "";
  return t;
}

function getApiErrorCode(err: any): string {
  const raw = err?.response?.data?.code;
  return typeof raw === "string" ? raw.trim().toLowerCase() : "";
}

function Login() {
  const { t, i18n } = useTranslation();
  const uiLang = i18n.resolvedLanguage || i18n.language;
  const queryClient = useQueryClient();
  const { setObjectId, setUser } = useAppContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectAfterAuth = safeInternalPath(searchParams.get("redirect"));
  const isExplorerAuthUi =
    searchParams.get("explorer") === "1" || searchParams.get("flow") === "explorer";
  const [mode, setMode] = useState<"login" | "register">("login");
  const [registerPhase, setRegisterPhase] = useState<RegisterPhase>("account");
  const [signupProfile, setSignupProfile] = useState<SignupProfileFormValues>(EMPTY_SIGNUP_PROFILE);
  const [signupProfileShowErrors, setSignupProfileShowErrors] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setisSuccess] = useState("");
  const [loginEmailError, setLoginEmailError] = useState<string | null>(null);
  const [loginPasswordError, setLoginPasswordError] = useState<string | null>(null);

  // console.log('this is an isSuccess',isSuccess);
  /** True when register returned 409 (email already exists) and we moved to the verify step. */
  const [verifyStepFromDuplicateEmail, setVerifyStepFromDuplicateEmail] = useState(false);
  /** True when login returned email_not_verified and we moved to the verify step. */
  const [verifyStepFromLogin, setVerifyStepFromLogin] = useState(false);
  const socialCallbackHandledRef = useRef(false);

  const isRegisterAccountStep = mode === "register" && registerPhase === "account";
  const { exists: emailExists, needsEmailVerification, checking: emailExistsChecking } =
    useDebouncedEmailExistsCheck(email, isRegisterAccountStep);

  const getLocalizedError = useCallback(
    (err: any, fallbackKey: string, codePrefix = "auth.errorCode"): string => {
      const code = getApiErrorCode(err);
      if (code) {
        const codeKey = `${codePrefix}.${code}`;
        if (i18n.exists(codeKey)) return t(codeKey);
      }
      return t(fallbackKey);
    },
    [i18n, t]
  );

  useEffect(() => {
    const modeParam = searchParams.get("mode");
    const emailParam = searchParams.get("email");
    const fn = searchParams.get("prefillFirstName");
    const ln = searchParams.get("prefillLastName");

    if (emailParam && isValidEmail(emailParam)) {
      setEmail(emailParam);
    }
    if (fn) setFirstName(fn);
    if (ln) setLastName(ln);

      if (modeParam === "register") {
      setMode("register");
      if (emailParam && isValidEmail(emailParam)) {
        setRegisterPhase("account");
      } else {
        setRegisterPhase("account");
      }
    }
  }, [searchParams]);

  const resetRegisterFlow = useCallback(() => {
    setRegisterPhase("account");
    setSignupProfile(EMPTY_SIGNUP_PROFILE);
    setSignupProfileShowErrors(false);
    setVerificationCode("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setError(null);
    setInfo(null);
    setVerifyStepFromDuplicateEmail(false);
    setVerifyStepFromLogin(false);
  }, []);

  const switchToLogin = useCallback(() => {
    setMode("login");
    resetRegisterFlow();
    setShowForgotPassword(false);
    setError(null);
    setisSuccess("");
    setLoginEmailError(null);
    setLoginPasswordError(null);
    setInfo(null);
  }, [resetRegisterFlow]);

  const switchToRegister = useCallback(() => {
    setMode("register");
    setRegisterPhase("account");
    setSignupProfile(EMPTY_SIGNUP_PROFILE);
    setSignupProfileShowErrors(false);
    setShowForgotPassword(false);
    setError(null);
    setInfo(null);
    setPassword("");
    setFirstName("");
    setLastName("");
    setVerificationCode("");
    setVerifyStepFromDuplicateEmail(false);
    setVerifyStepFromLogin(false);
  }, []);

  const canLoginSubmit = useMemo(
    () => email.trim().length > 0 && password.trim().length > 0,
    [email, password]
  );

  const canRegisterAccountSubmit = useMemo(
    () =>
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      isStrongPassword(password.trim()) &&
      isValidEmail(email),
    [email, firstName, lastName, password]
  );

  const canRegisterProfileSubmit = useMemo(
    () => isSignupProfileComplete(signupProfile),
    [signupProfile]
  );
  const registerPasswordFeedback = useMemo(
    () => getPasswordRequirementFeedback(password),
    [password, uiLang]
  );

  const resetPasswordFeedback = useMemo(
    () => getPasswordRequirementFeedback(newPassword),
    [newPassword, uiLang]
  );

  /**
   * Post-login / verify destination. OAuth/social return strips `?redirect=` / `explorer=1` from the URL,
   * so membership+event combo must rely on session (see ExplorerCheckout goMembershipFirst).
   * Normal portal login (no Explorer flags, no pending combo) → `redirect` or /dashboard only.
   */
  const resolvePostAuthPath = useCallback(async (objectId?: string) => {
    const pendingMembershipEventCombo = !!getExplorerPostMembershipEventCombo();
    if (pendingMembershipEventCombo) {
      return "/register-user";
    }
    const explorerEventId = getExplorerEventId();
    const isExplorerContext = isExplorerAuthUi || !!explorerEventId;
    if (objectId?.trim()) {
      try {
        const { data } = await axiosInstance.get(
          getAPIURL(`/User/ByObjectId/${encodeURIComponent(objectId.trim())}`),
        );
        const user = data?.user ?? data?.User;
        if (shouldResumeEarlyStageRegistration(null, user)) {
          return "/register-user";
        }
        try {
          const esStatus = await getEarlyStageStatus();
          const userObjectId =
            (typeof user?.objectId === "string" && user.objectId.trim()) ||
            (typeof user?.ObjectId === "string" && user.ObjectId.trim()) ||
            objectId.trim();
          if (isEarlyStageRejectionNoticeVisible(esStatus, userObjectId)) {
            return "/register-user";
          }
        } catch {
          /* not early stage or status unavailable */
        }
        if (!isExplorerContext && !isPaidPortalMember(user)) {
          return "/event-booking";
        }
      } catch (err) {
        if (!isExplorerContext && isUserDoesNotExistError(err)) {
          return "/register-user";
        }
      }
    } else if (!isExplorerContext) {
      return "/register-user";
    }
    let destination = redirectAfterAuth || "/dashboard";
    if (isExplorerAuthUi || explorerEventId) {
      if (explorerEventId && redirectAfterAuth === "/event-booking") {
        destination = `/confirm-booking/${explorerEventId}`;
      } else if (explorerEventId && !redirectAfterAuth) {
        destination = `/confirm-booking/${explorerEventId}`;
      }
      destination = await resolveExplorerGuardedPath(objectId, destination, {
        explorerContext: true,
      });
    }
    return destination;
  }, [isExplorerAuthUi, redirectAfterAuth]);

  /** Pushes explorer profile + pending step-2 data after successful sign-in. */
  const syncExplorerOnOtpVerify = useCallback(async () => {
    const pending = getExplorerPendingOnboarding();
    const local = getExplorerLocalProfile();
    const explorerEventId = getExplorerEventId();
    if (!isExplorerAuthUi && !pending?.segment && !local && !explorerEventId) return;
    await syncExplorerStateAfterAuth();
  }, [isExplorerAuthUi]);

  const applyAuthSession = useCallback(
    (result: {
      accessToken: string;
      refreshToken: string;
      expiresAtUtc?: string;
      userObjectId: string;
      email?: string;
    }) => {
      clearAuthSensitiveQueries(queryClient);
      setUser(undefined);
      setTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAtUtc: result.expiresAtUtc,
        userObjectId: result.userObjectId,
        email: result.email,
      });
      setObjectId(result.userObjectId);
    },
    [queryClient, setObjectId, setUser],
  );

  const handleExplorerSyncFailure = useCallback(
    (err: unknown): boolean => {
      if (err instanceof ExplorerProfileSyncError) {
        setError(
          err.message ||
            t("explorer.segmentation.saveFailed", {
              defaultValue: "Could not save your explorer profile. Please complete signup.",
            }),
        );
        navigate(explorerSignupPath(), { replace: true });
        return true;
      }
      const code = (err as { response?: { data?: { code?: string } } })?.response
        ?.data?.code;
      if (code === "profile_required" || code === "email_mismatch" || code === "profile_incomplete") {
        setError(
          t("explorer.segmentation.profileMissing", {
            defaultValue: "Please complete your profile before continuing.",
          }),
        );
        navigate(explorerSignupPath(), { replace: true });
        return true;
      }
      return false;
    },
    [navigate, t],
  );

  const onLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setisSuccess("");
    setLoginEmailError(null);
    setLoginPasswordError(null);
    setInfo(null);
    setLoading(true);
    try {
      const result = await loginApi(email.trim(), password.trim());
      applyAuthSession(result);
      try {
        await syncExplorerOnOtpVerify();
      } catch (syncErr) {
        if (handleExplorerSyncFailure(syncErr)) return;
        throw syncErr;
      }
      navigate(await resolvePostAuthPath(result.userObjectId), { replace: true });
    } catch (err: any) {
      const apiCode = getApiErrorCode(err);
      const apiMessage = err?.response?.data?.error;

      if (apiCode === "email_not_verified") {
        setError(null);
        setisSuccess("");
        setMode("register");
        setRegisterPhase("verify");
        setVerificationCode("");
        setVerifyStepFromDuplicateEmail(false);
        setVerifyStepFromLogin(true);
        setInfo(t("auth.emailAlreadyExistsVerify"));
      } else if (apiCode === "password_not_set") {
        setError(null);
        setisSuccess("");
        setInfo(t("auth.useForgotPasswordHint"));
        setShowForgotPassword(true);
      } else if (apiCode === "account_not_found") {
        setLoginEmailError("auth.errorCode.account_not_found");
      } else if (apiCode === "invalid_credentials") {
        setLoginPasswordError("auth.errorCode.invalid_credentials");
      } else {
        setisSuccess(typeof apiMessage === "string" && apiMessage.trim() ? apiMessage.trim() : "");
        setError(getLocalizedError(err, "auth.loginFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const onRegisterFromAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!canRegisterAccountSubmit) {
      if (!isStrongPassword(password.trim())) {
        setError(t("auth.passwordRule"));
      } else if (!isValidEmail(email)) {
        setError(t("auth.enterValidEmail"));
      }
      return;
    }
    if (emailExistsChecking) {
      return;
    }
    if (emailExists === true && !needsEmailVerification) {
      return;
    }
    if (needsEmailVerification) {
      setLoading(true);
      try {
        await resendVerificationEmailApi(email.trim());
        setRegisterPhase("verify");
        setVerificationCode("");
        setVerifyStepFromDuplicateEmail(true);
        setVerifyStepFromLogin(false);
        setInfo(t("auth.emailAlreadyExistsVerify"));
      } catch (err: any) {
        setError(getLocalizedError(err, "auth.resendCodeFailed"));
      } finally {
        setLoading(false);
      }
      return;
    }
    setRegisterPhase("profile");
  };

  const onRegisterFromProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!isSignupProfileComplete(signupProfile)) {
      setSignupProfileShowErrors(true);
      setError(t("explorer.segmentation.completeRequired"));
      return;
    }
    if (!isValidPhoneNumber(signupProfile.phoneNumber)) {
      setSignupProfileShowErrors(true);
      setError(t("explorer.signup.phoneInvalid"));
      return;
    }
    setSignupProfileShowErrors(false);
    setLoading(true);
    try {
      if (isExplorerAuthUi) {
        await trySyncExplorerPublicProfileBeforeSignup({
          emailForSignup: email.trim(),
        });
      }
      const start = await registerApi({
        email: email.trim(),
        password: password.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: signupProfile.phoneNumber.trim().replace(/\s/g, ""),
        city: signupProfile.city.trim(),
        country: signupProfile.country.trim(),
        jobTitle: signupProfile.jobTitle.trim(),
        companyName: signupProfile.companyName.trim(),
        sector: signupProfile.sector.trim(),
        explorerAttendeeType:
          normalizeExplorerSegment(signupProfile.explorerAttendeeType) ||
          signupProfile.explorerAttendeeType.trim(),
        objectivesForAttending: signupProfile.objectivesForAttending.trim(),
        stageOfBusiness:
          signupProfile.stageOfBusiness.trim() ||
          stageLabelForExplorerSegment(signupProfile.explorerAttendeeType),
        sectorsOfInterest: signupProfile.sectorsOfInterest.join(", "),
        photoSharingConsent: signupProfile.photoSharingConsent,
        termsAttachmentConsent: signupProfile.termsAttachmentConsent,
        gdprConsent: signupProfile.gdprConsent,
        marketingConsent: signupProfile.marketingConsent,
      });
      try {
        const raw = localStorage.getItem("UserData");
        const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
        localStorage.setItem(
          "UserData",
          JSON.stringify({
            ...parsed,
            email: email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          }),
        );
      } catch {
        /* ignore corrupt storage */
      }
      if (start.requiresEmailVerification) {
        setRegisterPhase("verify");
        setVerificationCode("");
        setVerifyStepFromDuplicateEmail(false);
        setVerifyStepFromLogin(false);
      }
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError(null);
        setInfo(null);
        setMode("login");
        setRegisterPhase("account");
        setVerificationCode("");
        setVerifyStepFromDuplicateEmail(false);
        setVerifyStepFromLogin(false);
        setisSuccess(t("auth.emailAlreadyExistsLogin"));
      } else {
        setError(getLocalizedError(err, "auth.registrationFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const onVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const code = verificationCode.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setError(t("auth.enterSixDigitCode"));
      return;
    }
    setLoading(true);
    try {
      const result = await verifyEmailApi({ email: email.trim(), code });
      applyAuthSession(result);
      try {
        await syncExplorerOnOtpVerify();
      } catch (syncErr) {
        if (handleExplorerSyncFailure(syncErr)) return;
        throw syncErr;
      }
      navigate(await resolvePostAuthPath(result.userObjectId), { replace: true });
    } catch (err: any) {
      setError(getLocalizedError(err, "auth.verificationFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onResendVerificationEmail = async () => {
    if (!isValidEmail(email)) {
      setError(t("auth.enterValidEmail"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await resendVerificationEmailApi(email.trim());
      setInfo(t("auth.verificationCodeResent"));
    } catch (err: any) {
      setError(getLocalizedError(err, "auth.resendCodeFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setisSuccess("");
    setInfo(null);
    setLoading(true);
    try {
      await forgotPasswordApi(email.trim());
      setInfo(t("auth.resetCodeSent"));
      setResetCodeSent(true);
    } catch (err: any) {
      setError(getLocalizedError(err, "auth.requestFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setisSuccess("");
    setInfo(null);
    if (!isStrongPassword(newPassword.trim())) {
      setError(t("auth.passwordRule"));
      return;
    }
    setLoading(true);
    try {
      await resetPasswordApi({
        email: email.trim(),
        code: resetCode.trim(),
        newPassword,
      });
      setInfo(t("auth.passwordResetSuccess"));
      setShowForgotPassword(false);
      setResetCode("");
      setNewPassword("");
      setShowNewPassword(false);
      setResetCodeSent(false);
      setMode("login");
    } catch (err: any) {
      const apiMessage = err?.response?.data?.error;
      setError(
        typeof apiMessage === "string" && apiMessage.trim()
          ? apiMessage.trim()
          : getLocalizedError(err, "auth.resetFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const completeSocialLogin = useCallback(
    async (
      payload:
        | { provider: "google"; idToken: string }
        | { provider: "linkedin"; accessToken: string }
        | { provider: "apple"; idToken: string }
    ) => {
      setError(null);
      setLoading(true);
      try {
        const result = await socialLoginApi(payload);
        applyAuthSession(result);
        try {
          await syncExplorerOnOtpVerify();
        } catch (syncErr) {
          if (handleExplorerSyncFailure(syncErr)) return;
          throw syncErr;
        }
        navigate(await resolvePostAuthPath(result.userObjectId), { replace: true });
      } catch (err: any) {
        setError(getLocalizedError(err, "auth.socialLoginFailed"));
      } finally {
        setLoading(false);
      }
    },
    [
      navigate,
      applyAuthSession,
      resolvePostAuthPath,
      syncExplorerOnOtpVerify,
      handleExplorerSyncFailure,
      getLocalizedError,
    ]
  );

  useEffect(() => {
    if (socialCallbackHandledRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const provider = (params.get("provider") || params.get("social_provider") || "").trim().toLowerCase();
    const idToken = (params.get("id_token") || params.get("idToken") || "").trim();
    const accessToken = (params.get("access_token") || params.get("accessToken") || "").trim();
    const providerError = params.get("error_description") || params.get("error");

    if (providerError) {
      socialCallbackHandledRef.current = true;
      setError(t("auth.socialLoginFailed"));
      return;
    }

    if (!provider) {
      return;
    }

    socialCallbackHandledRef.current = true;
    window.history.replaceState({}, document.title, window.location.pathname);

    if (provider === "google" && idToken) {
      void completeSocialLogin({ provider: "google", idToken });
      return;
    }

    if (provider === "linkedin" && accessToken) {
      void completeSocialLogin({ provider: "linkedin", accessToken });
      return;
    }

    if (provider === "apple" && idToken) {
      void completeSocialLogin({ provider: "apple", idToken });
      return;
    }

    setError(t("auth.socialTokenMissing"));
  }, [completeSocialLogin, t]);

  const registerAccountBlocked = emailExists === true && !needsEmailVerification;
  const isProfileRegisterStep = mode === "register" && registerPhase === "profile";
  const isCompact = showForgotPassword && resetCodeSent;

  return (
    <div
      className={`auth-flow-root position-relative d-flex justify-content-center overflow-x-hidden vh-100${
        isProfileRegisterStep ? " auth-flow-root--profile overflow-hidden align-items-stretch" : " overflow-y-auto align-items-start"
      }${isCompact ? " auth-flow-root--compact py-1 px-2" : " py-1 py-md-2 px-2 px-md-3"}`}
    >
      <div
        className="position-absolute top-0 end-0 m-3 z-3"
      >
        <LiveTranslationDropdown variant="on-dark" />
      </div>
      <div className="position-fixed top-0 start-0 end-0 bottom-0 z-0 pe-none" aria-hidden />
      <div
        className={`auth-flow-card position-relative z-1 w-100 bg-white rounded-4 shadow border${
          isProfileRegisterStep ? "" : " my-auto"
        }${
          isCompact ? " auth-flow-card--compact p-3" : " p-2 p-md-3 px-md-4 py-md-3"
        }${isProfileRegisterStep ? " auth-flow-card--profile" : ""}`}
      >
        <div className="text-center mb-2 mb-md-3">
          <div className="d-inline-flex align-items-center justify-content-center p-0 mb-0 col-5 col-md-auto">
            <img
              src="/connect-logo.png"
              alt="SMBF logo"
              className="auth-flow-brand-logo d-block w-100 h-auto"
            />
          </div>
          {isExplorerAuthUi ? (
            <>
              {!(mode === "register" && registerPhase === "verify") ? (
                <p className="small text-secondary lh-sm mb-0 px-md-2">
                  {t("auth.explorerPasswordPrompt")}
                </p>
              ) : null}
            </>
          ) : null}
        </div>

        {isSuccess || error ? (
          <div className="alert alert-danger p-2 mb-2 small lh-sm">{isSuccess || error}</div>
        ) : null}
        {info ? (
          <div className={`alert alert-success text-center small lh-sm ${isCompact ? "p-2 mb-2" : "p-3 mb-3"}`}>{info}</div>
        ) : null}

        {showForgotPassword ? (
          <form onSubmit={resetCodeSent ? onResetPassword : onForgotPassword}>
            {resetCodeSent ? (
              <>
                <h1 className="auth-flow-title h5 fw-semibold text-dark mb-2">{t("auth.resetPassword")}</h1>
                <p className="small text-secondary lh-sm mb-3">
                  {t("auth.emailCodeNotice")} <strong>{email.trim()}</strong>
                </p>
              </>
            ) : (
              <>
                <h1 className="h5 fw-semibold text-body mb-2 text-center">{t("auth.resetPassword")}</h1>
                <p className="small text-secondary lh-sm mb-4 text-center">{t("auth.emailCodeNotice")}</p>
                <div className="mb-4">
                  <label className="form-label small fw-semibold text-secondary">{t("auth.email")}</label>
                  <input
                    key={`auth-email-forgot-${uiLang}`}
                    className="form-control"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.emailPlaceholder")}
                    autoComplete="email"
                  />
                </div>
              </>
            )}

            {resetCodeSent ? (
              <>
                <div className="mb-3">
                  <label htmlFor="otp-digit-0" className="form-label small fw-semibold text-secondary">
                    {t("auth.resetCode")}
                  </label>
                  <OtpInput value={resetCode} onChange={setResetCode} disabled={loading} autoFocus />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold text-secondary">{t("auth.newPassword")}</label>
                  <div className="input-group">
                    <input
                      className="form-control"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t("auth.passwordPlaceholder")}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowNewPassword((s) => !s)}
                      aria-label={showNewPassword ? t("auth.ariaHidePassword") : t("auth.ariaShowPassword")}
                    >
                      {showNewPassword ? t("auth.hide") : t("auth.show")}
                    </button>
                  </div>
                  <PasswordRequirementFeedback feedback={resetPasswordFeedback} />
                </div>
                <button className="btn btn-primary w-100 py-2 px-3 rounded-3 fw-semibold text-nowrap" disabled={loading || !resetCode || !isStrongPassword(newPassword.trim())} type="submit">
                  {loading ? t("auth.pleaseWait") : t("auth.resetPassword")}
                </button>
              </>
            ) : (
              <button className="btn btn-primary w-100 py-2 px-3 rounded-3 fw-semibold text-nowrap mt-1" disabled={loading || !email.trim()} type="submit">
                {loading ? t("auth.pleaseWait") : t("auth.sendResetCode")}
              </button>
            )}

            <button
              type="button"
              className={`btn btn-link w-100 auth-flow-link fw-semibold text-decoration-underline ${resetCodeSent ? "mt-2 py-1" : "mt-4"}`}
              onClick={() => {
                setShowForgotPassword(false);
                setResetCodeSent(false);
                setResetCode("");
                setNewPassword("");
                setShowNewPassword(false);
                setInfo(null);
                setError(null);
                setisSuccess("");
              }}
            >
              {t("auth.backToLogin")}
            </button>
          </form>
        ) : mode === "register" && registerPhase === "verify" ? (
          <form onSubmit={onVerifyEmail}>
            <h1 className="h5 fw-semibold text-body mb-2 text-center">{t("auth.verificationCodeSentTitle")}</h1>
            <p className="small text-secondary lh-sm mb-4 text-center">
              {t("auth.verifyEmailInstructionStart")} <strong>{email.trim()}</strong>
            </p>

            <label htmlFor="otp-digit-0" className="visually-hidden">
              {t("auth.verificationCodeLabel")}
            </label>
            <OtpInput value={verificationCode} onChange={setVerificationCode} disabled={loading} autoFocus />

            <div className="d-grid gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary w-100 rounded-3 fw-semibold lh-sm py-2 px-3"
                disabled={loading || !isValidEmail(email)}
                onClick={() => void onResendVerificationEmail()}
              >
                {t("auth.resendVerificationCode")}
              </button>

              {verifyStepFromDuplicateEmail || verifyStepFromLogin ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 rounded-3 fw-semibold lh-sm py-2 px-3"
                  disabled={loading}
                  onClick={() => {
                    setVerifyStepFromDuplicateEmail(false);
                    setVerifyStepFromLogin(false);
                    setInfo(null);
                    switchToLogin();
                  }}
                >
                  {t("auth.logIn")}
                </button>
              ) : null}

              <button
                className="btn btn-primary w-100 rounded-3 fw-semibold py-2"
                disabled={loading || verificationCode.replace(/\D/g, "").length !== 6}
                type="submit"
              >
                {loading ? t("auth.verifying") : t("auth.verify")}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary w-100 rounded-3 fw-semibold lh-sm py-2 px-3"
                disabled={loading}
                onClick={() => {
                  setVerificationCode("");
                  setError(null);
                  setInfo(null);
                  setVerifyStepFromDuplicateEmail(false);
                  if (verifyStepFromLogin) {
                    setVerifyStepFromLogin(false);
                    switchToLogin();
                  } else {
                    setRegisterPhase("profile");
                  }
                }}
              >
                {t("auth.back")}
              </button>
            </div>
          </form>
        ) : mode === "register" && registerPhase === "profile" ? (
          <form onSubmit={onRegisterFromProfile} className="auth-signup-profile-form">
            <div className="auth-signup-profile-scroll">
              <SignupProfileStep
                values={signupProfile}
                onChange={(patch) => setSignupProfile((prev) => ({ ...prev, ...patch }))}
                showFieldErrors={signupProfileShowErrors}
              />
            </div>

            <div className="auth-signup-actions mt-3 mt-md-4">
              <button
                className="btn btn-primary w-100 py-2 px-3 rounded-3 fw-semibold text-nowrap"
                disabled={!canRegisterProfileSubmit || loading}
                type="submit"
              >
                {loading ? t("auth.pleaseWait") : t("auth.continue")}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary w-100 py-2"
                disabled={loading}
                onClick={() => {
                  setSignupProfileShowErrors(false);
                  setRegisterPhase("account");
                }}
              >
                {t("auth.back")}
              </button>
            </div>
          </form>
        ) : mode === "register" && registerPhase === "account" ? (
          <form onSubmit={onRegisterFromAccount}>
            <h1 className="h5 fw-semibold text-body mb-2 text-center">{t("auth.getStarted")}</h1>
            <p className="small text-secondary lh-sm mb-4 text-center">{t("auth.createAccountPrompt")}</p>

            <div className="row g-2">
              <div className="col-12 col-md-6">
                <label className="form-label small fw-semibold text-secondary">{t("auth.firstName")}</label>
                <input
                  className="form-control rounded-3"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t("auth.firstName")}
                  autoComplete="given-name"
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small fw-semibold text-secondary">{t("auth.lastName")}</label>
                <input
                  className="form-control rounded-3"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t("auth.lastName")}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label small fw-semibold text-secondary">{t("auth.workEmail")}</label>
              <input
                key={`auth-email-register-${uiLang}`}
                className={`form-control rounded-3${registerAccountBlocked ? " is-invalid" : ""}`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")}
                autoComplete="email"
                aria-invalid={registerAccountBlocked}
              />
            </div>

            <div className="mt-3">
              <label className="form-label small fw-semibold text-secondary">{t("auth.password")}</label>
              <div className="input-group">
                <input
                  className="form-control rounded-start-3"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("auth.passwordPlaceholder")}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-end-3"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? t("auth.ariaHidePassword") : t("auth.ariaShowPassword")}
                >
                  {showPassword ? t("auth.hide") : t("auth.show")}
                </button>
              </div>
            </div>

            <PasswordRequirementFeedback feedback={registerPasswordFeedback} />

            {emailExistsChecking ? (
              <p className="small text-muted mb-2 mt-2">{t("auth.checkingEmail")}</p>
            ) : null}
            {needsEmailVerification ? (
              <p className="small text-muted mb-2 mt-2">{t("auth.emailAlreadyExistsVerify")}</p>
            ) : null}
            {registerAccountBlocked ? (
              <div className="invalid-feedback d-block small mb-2 mt-2">
                {t("auth.emailAlreadyExistsLogin")}{" "}
                <button type="button" className="auth-flow-link fw-semibold p-0 border-0 bg-transparent align-baseline" onClick={switchToLogin}>
                  {t("auth.logIn")}
                </button>
              </div>
            ) : null}

            <button
              className="btn btn-primary w-100 py-2 px-3 rounded-3 fw-semibold text-nowrap mt-3"
              type="submit"
              disabled={loading || !canRegisterAccountSubmit || registerAccountBlocked || emailExistsChecking}
            >
              {loading || emailExistsChecking ? t("auth.pleaseWait") : t("auth.continue")}
            </button>

            <p className="small text-secondary lh-sm mb-0 mt-3">{t("auth.realEmailHelp")}</p>

            {/*
            <div className="d-flex align-items-center gap-3 my-3 text-secondary small">
              <hr className="flex-grow-1 my-0 border-secondary-subtle" />
              <span className="flex-shrink-0">Or continue with</span>
              <hr className="flex-grow-1 my-0 border-secondary-subtle" />
            </div>

            <SocialLoginButtons
              disabled={loading}
              onGoogleIdToken={(idToken) => void completeSocialLogin({ provider: "google", idToken })}
            />
            */}

            <p className="text-center small text-secondary lh-sm mt-4 mb-0">
              {t("auth.alreadyHaveAccount")}{" "}
              <button
                type="button"
                className="btn btn-link auth-flow-link fw-semibold p-0 align-baseline"
                onClick={switchToLogin}
              >
                {t("auth.logIn")}
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={onLoginSubmit}>
            <h1 className="h5 fw-semibold text-body mb-2 text-center">{t("auth.welcomeBack")}</h1>
            <p className="small text-secondary lh-sm mb-4 text-center">{t("auth.signInPrompt")}</p>

            <div className="mb-3">
              <label className="form-label small fw-semibold text-secondary">{t("auth.email")}</label>
              <input
                key={`auth-email-login-${uiLang}`}
                className={`form-control${loginEmailError ? " is-invalid" : ""}`}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (loginEmailError) setLoginEmailError(null);
                }}
                placeholder={t("auth.emailPlaceholder")}
                autoComplete="email"
                aria-invalid={!!loginEmailError}
              />
              {loginEmailError ? <div className="invalid-feedback d-block">{t(loginEmailError)}</div> : null}
            </div>
            <div className="mb-2">
              <label className="form-label small fw-semibold text-secondary">{t("auth.password")}</label>
              <input
                className={`form-control${loginPasswordError ? " is-invalid" : ""}`}
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (loginPasswordError) setLoginPasswordError(null);
                }}
                placeholder={t("auth.passwordPlaceholder")}
                autoComplete="current-password"
                aria-invalid={!!loginPasswordError}
              />
              {loginPasswordError ? <div className="invalid-feedback d-block">{t(loginPasswordError)}</div> : null}
            </div>

            <div className="d-flex justify-content-end mb-3">
              <button
                type="button"
                className="btn btn-link auth-flow-link small fw-semibold p-0"
                onClick={() => {
                  setShowForgotPassword(true);
                  setInfo(null);
                  setError(null);
                  setisSuccess("");
                  setLoginEmailError(null);
                  setLoginPasswordError(null);
                }}
                disabled={loading}
              >
                {t("auth.forgotPassword")}
              </button>
            </div>

            <button className="btn btn-primary w-100 py-2 px-3 rounded-3 fw-semibold text-nowrap" disabled={!canLoginSubmit || loading} type="submit">
              {loading ? t("auth.pleaseWait") : t("auth.logIn")}
            </button>

            <div className="d-flex align-items-center gap-3 my-3 text-secondary small">
              <hr className="flex-grow-1 my-0 text-secondary-emphasis opacity-25" />
              <span className="flex-shrink-0">{t("auth.orContinueWith")}</span>
              <hr className="flex-grow-1 my-0 text-secondary-emphasis opacity-25" />
            </div>

            {/* <SocialLoginButtons
              disabled={loading}
              onGoogleIdToken={(idToken) => void completeSocialLogin({ provider: "google", idToken })}
            /> */}

            <p className="text-center small text-secondary lh-sm mt-4 mb-0">
              {t("auth.newHere")}{" "}
              <button
                type="button"
                className="btn btn-link auth-flow-link fw-semibold p-0 align-baseline"
                onClick={switchToRegister}
              >
                {t("auth.createAccount")}
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;
